<template>
  <div class="content p-5" v-show="question.isVisible">
    <div class="row" >
      <div class="col">
        <span class="mr-3 question-name" v-html="question.questionName"></span>
      </div>
      <div v-if="question.questionInformationText || question.questionInformationImages?.length > 0" class="col--auto">
        <smtt-icon icon="info-circle" color="#4f23c9" class="information-icon" @click="isInformationModalVisible = true" />
      </div>
    </div>
    <div class="row my-3">
      <div class="col">
        <div class="question-instruction" v-html="question.questionInstructions"></div>
      </div>
    </div>
    <div v-if="question.questionType === 0">
      <OpenAnswerOption
        :value-type="0"
        :answer-name="question.answerName"
        :is-required="question.mandatory === 1 "
        :view-mode="viewMode"
        :custom-validation="question.questionCustomValidation"
        @onAnswered="openAnswerHandler"
      />
    </div>
    <div v-if="question.questionType === 1">
      <OpenAnswerOption
        :value-type="1"
        :answer-name="question.answerName"
        :is-required="question.mandatory === 1"
        :view-mode="viewMode"
        :custom-validation="question.questionCustomValidation"
        @onAnswered="openAnswerHandler"
      />
    </div>
    <div v-if="question.questionType === 2">
      <SingleOption
        :question-options="question.questionOptions"
        :question-id="question.questionId"
        :answer-name="question.answerName"
        :view-mode="viewMode"
        @onAnswered="singleOptionHandler"
        @onShowConditional="showConditional"
        @onHideConditional="hideConditional"
      />
    </div>
    <div v-if="question.questionType === 3">
      <MultipleOption
        :question-options="question.questionOptions"
        :answer-name="question.answerName"
        :view-mode="viewMode"
        @onAnswered="multipleOptionHandler"
        @onShowHideConditional="showHideConditional"
      />
    </div>
    <div v-if="question.questionType === 4">
      <TakePicture
        :formats="question.format"
        :selected-images="question.image"
        :view-mode="viewMode"
        @onAnswered="takePictureHandler"
      />
    </div>
    <div v-if="question.questionType === 5">
      <OpenAnswerBoxOption
        :answer-name="question.answerName"
        :view-mode="viewMode"
        :custom-validation="question.questionCustomValidation"
        @onAnswered="openAnswerHandler"
      />
    </div>
    <div v-if="question.questionType === 6">
      <SelectionOption
        :question-options="question.questionOptions"
        :question-id="question.questionId"
        :view-mode="viewMode"
        @onAnswered="selectionOptionHandler"
        @onShowConditional="showConditional"
        @onHideConditional="hideConditional"
      />
    </div>
    <div v-if="question.questionType === 9">
      <time-question
        style="max-width: 200px"
        :view-mode="viewMode"
        :answer-name="question.answerName"
        :is-required="question.mandatory === 1"
        @onAnswered="timeQuestionHandler"
      />
    </div>
    <div v-if="question.questionType === 10">
      <selection-option-search
        :question-options="question.questionOptions"
        :question-id="question.questionId"
        :view-mode="viewMode"
        @onAnswered="selectionOptionHandler"
        @onShowConditional="showConditional"
        @onHideConditional="hideConditional"
      />
    </div>
    <div v-if="question.questionType === 11">
      <chrono-question
        :view-mode="viewMode"
        :answer-name="question.answerName"
        @onAnswered="chronoQuestionHandler"
      />
    </div>
    <div v-if="question.questionType === 12 || question.questionType === 15 || question.questionType === 16 || question.questionType === 17">
      <NumericList
        :question-options="question.questionOptions"
        :max-length="question.maxLength"
        :question-id="question.questionId"
        :answer-name="question.answerName"
        :view-mode="viewMode"
        :custom-validation="question.questionCustomValidation"
        :validationMode="numericListValidationType"
        @onAnswered="numericListHandler"
      />
    </div>
    <div v-if="question.questionType === 13">
      <numeric-variable-list
          :question-options="question.questionOptions"
          :max-length="question.maxLength"
          :question-id="question.questionId"
          :view-mode="viewMode"
          @onAnswered="numericVariableListHandler"
      />
    </div>
    <div v-if="question.questionType === 18">
      <scan-qr
        :answer-name="question.answerName"
        :view-mode="viewMode"
        @onAnswered="openAnswerHandler"
      />
    </div>

    <!--Modal with question information-->
    <AppModal
      :title="$t('surveyQuestionScreen.question.informationModalTitle')"
      :show="isInformationModalVisible"
      @on-click-outside="isInformationModalVisible = false"
      @on-close-click="isInformationModalVisible = false"
      width="100%"
    >
      <span v-if="question.questionInformationText" v-html="question.questionInformationText"></span>
      <div v-if="question.questionInformationImages?.length > 0" class="mt-5">
        <div class="thumbnails-container">
          <img v-for="image in question.questionInformationImages" :key="image.name" :src="image.data" :alt="image.name" class="image-thumbnail mx-3 my-3" @click="showImageModal(image.data)"/>
        </div>
      </div>
    </AppModal>

    <!--Modal with image visualization-->
    <div class="modal-mask" v-if="showImageVisualization">
      <div class="modal-wrapper">
        <div class="title">
          <div style="width: 24px;"></div>
          <div class="h3 mb-0">
            {{ $t('surveyQuestionScreen.question.informationModal.imageViewTitle') }}
          </div>
          <div @click="showImageVisualization = false" role="button">
            <smtt-icon class="d-flex align-self-center" icon="close" />
          </div>
        </div>
        <div class="modal-body">
            <div class="image-container">
              <img :src="selectedImageSrc" alt="image" class="image" />
            </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OpenAnswerOption from './OpenAnswerOption';
import SingleOption from './SingleOption';
import MultipleOption from './MultipleOption';
import TakePicture from './TakePicture';
import SelectionOption from './SelectionOption';
import OpenAnswerBoxOption from './OpenAnswerBoxOption';
import TimeQuestion from "@/components/SurveyQuestionScreen/Questions/TimeQuestion.vue";
import SelectionOptionSearch from './SelectionOptionSearch.vue';
import ChronoQuestion from '@/components/SurveyQuestionScreen/Questions/ChronoQuestion'
import NumericList from "./NumericList.vue";
import NumericVariableList from "./NumericVariableList.vue";
import ScanQr from "./ScanQr";
import AppModal from "@/components/SharedComponents/AppModal";

export default {
  'name': 'Question',
  'components': {
    NumericList,
    TimeQuestion,
    OpenAnswerBoxOption,
    SelectionOption,
    TakePicture,
    MultipleOption,
    OpenAnswerOption,
    SingleOption,
    SelectionOptionSearch,
    ChronoQuestion,
    NumericVariableList,
    ScanQr,
    AppModal,
  },
  'props': {
    'question': {
      type: Object,
      required: true
    },
    'viewMode': {
      'type': Boolean,
      'required': false
    }
  },
  data() {
    return {
      answeredQuestion: {},
      isInformationModalVisible: false,
      showImageVisualization: false,
      selectedImageSrc: null,
    }
  },
  computed: {
    numericListValidationType() {
      switch(this.question.questionType) {
        case 15:
          return 'sum'
        case 16:
          return 'nonzero'
        case 17:
          return 'top'
        default:
          return null
      }
    }
  },
  'methods': {
    showHideConditional(option, show) {
      this.$emit('onShowHideConditional', option, show)
    },
    showConditional(option, show) {
      this.$emit('onShowHideConditional', option, show)
    },
    hideConditional(options) {
      options.forEach((option) => {
        if (option.conditional) {
          this.$emit('onShowHideConditional', option, false)
        }
      })
    },
    singleOptionHandler(e, text) {
      if (text === null) {
        this.answeredQuestion = { ...this.question, 'questionOptions': e, 'answerName': text, 'answerStatus': 2 }
      } else {
        if (this.question.mandatory === 1 && text !== '') {
          this.answeredQuestion = { ...this.question, 'questionOptions': e, 'answerName': text, 'answerStatus': 2 }
        } else {
          this.answeredQuestion = { ...this.question, 'questionOptions': e, 'answerName': text, 'answerStatus': 1 }
        }
      }
      this.event(this.answeredQuestion)
    },
    numericListHandler(questionOptions) {
      let notAnsweredQuestionOptions = questionOptions.filter((questionOption) => {
        return !(questionOption.text)
      })

      if (notAnsweredQuestionOptions.length > 0) {
        this.answeredQuestion = { ...this.question, 'questionOptions': questionOptions, 'answerStatus': 1 }
      } else {
        this.answeredQuestion = { ...this.question, 'questionOptions': questionOptions, 'answerStatus': 2 }
      }

      this.event(this.answeredQuestion)
    },
    numericVariableListHandler(questionOptions) {
      const notAnsweredQuestionOptions = questionOptions.some((questionOption) => {
        return !questionOption.text || questionOption.text.split(';').length !== 2;
      });

      if (notAnsweredQuestionOptions) {
        this.answeredQuestion = { ...this.question, 'questionOptions': questionOptions, 'answerStatus': 1 }
      } else {
        this.answeredQuestion = { ...this.question, 'questionOptions': questionOptions, 'answerStatus': 2 }
      }

      this.event(this.answeredQuestion);
    },
    'openAnswerHandler': function (value) {
      if (value) {
        this.answeredQuestion = { ...this.question, 'answerName': value, 'answerStatus': 2 }
      } else {
        if (this.question.mandatory === 1) {
          this.answeredQuestion = { ...this.question, 'answerName': value, 'answerStatus': 1 }
        } else {
          this.answeredQuestion = { ...this.question, 'answerName': value, 'answerStatus': 2 }
        }
      }
      this.event(this.answeredQuestion);
    },
    multipleOptionHandler(e, otherText) {
      let answerStatus = 0
      const optionIsSelected = e.filter((option) => option.selected === true)
      if (optionIsSelected.length === 0) answerStatus = 1
      else {
        // if otherText != null, means the 'other' option was checked
        if (otherText !== null) {
          if ((this.question.mandatory && otherText !== '') || !this.question.mandatory) {
            answerStatus = 2
          } else {
            answerStatus = 1
          }
        } else {
          answerStatus = 2
        }
      }

      this.answeredQuestion = { ...this.question, 'questionOptions': e, 'answerName': otherText, 'answerStatus': answerStatus }
      this.event(this.answeredQuestion)
    },
    'takePictureHandler': function (imagesConverted) {
      if (imagesConverted.length > 0) {
        const empty = imagesConverted.every((image) => image.id && image.deleted === true);
        if (empty && this.question.mandatory === 1) {
          this.answeredQuestion = { ...this.question, 'image': imagesConverted, 'answerStatus': 1 };
        } else {
          this.answeredQuestion = { ...this.question, 'image': imagesConverted, 'answerStatus': 2 };
        }
      } else {
        if (this.question.mandatory === 1) {
          this.answeredQuestion = { ...this.question, 'image': [], 'answerStatus': 1 };
        } else {
          this.answeredQuestion = { ...this.question, 'image': [], 'answerStatus': 2 };
        }
      }
      this.event(this.answeredQuestion)
    },
    selectionOptionHandler(e) {
      const answerStatus = e.some((option) => option.selected) ? 2 : 0;
      this.answeredQuestion = { ...this.question, 'questionOptions': e, 'answerStatus': answerStatus }
      this.event(this.answeredQuestion)
    },
    timeQuestionHandler(value) {
      if (value) {
        this.answeredQuestion = {...this.question, 'answerName': value, 'answerStatus': 2}
      } else {
        if (this.question.mandatory === 1) {
          this.answeredQuestion = {...this.question, 'answerName': value, 'answerStatus': 1}
        } else {
          this.answeredQuestion = {...this.question, 'answerName': value, 'answerStatus': 2}
        }
      }
      this.event(this.answeredQuestion);
    },
    chronoQuestionHandler(value) {
      if (value) {
        this.answeredQuestion = {...this.question, 'answerName': value, 'answerStatus': 2}
      }

      this.event(this.answeredQuestion);
    },
    event(e) {
      this.$emit('onAnswered', e)
    },
    showImageModal(imageSrc) {
      this.showImageVisualization = true;
      this.selectedImageSrc = imageSrc;
    }
  },
  created() {
    this.answeredQuestion = { ...this.question, 'answerStatus': 1 }
  }
}
</script>
<style scoped>
.question-instruction {
  line-height: 1rem;
}

.question-name {
  font-weight: bold;
  font-size: 16px;
}

.information-icon:hover {
  cursor: pointer;
}

.image {
  object-fit: contain;
  width: 100%;
  height: 100%;
}

.image-thumbnail {
  height: 100px;
  width: 100px;
  object-fit: contain;
  border-radius: 0.2rem;
  background-color: #fafafa;
}

.thumbnails-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: start;
}

.title {
  z-index: 1;
  position: absolute;
  top: 0;
  color: white;
  background-color: rgba(0, 0, 0, 0.5);
  width: 100%;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px;
}

.modal-mask {
    position: fixed;
    z-index: 50;
    width: 100%;
    height: 100%;
    transition: opacity 0.3s ease;
}

.modal-body {
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.image-container {
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
}
</style>
